export type TrendGranularity = 'hour' | 'day' | 'week';
export interface TrendPresentation {
  granularity?: TrendGranularity;
  timeZone?: string;
}

export function pickGranularity(dayCount: number): TrendGranularity {
  return dayCount <= 2 ? 'hour' : dayCount <= 92 ? 'day' : 'week';
}

// Calendar dates are not instants. Keep day/week labels on their original dates.
// Hour buckets are unique UTC instants, formatted using the response timezone.
export function formatTrendDate(
  date: string,
  granularity: TrendGranularity,
  timeZone: string,
  language: string,
  tooltip = false,
) {
  return new Intl.DateTimeFormat(language, {
    month: 'numeric',
    day: '2-digit',
    ...(tooltip ? { year: 'numeric' as const } : {}),
    ...(granularity === 'hour'
      ? {
          hour: '2-digit' as const,
          minute: '2-digit' as const,
          hourCycle: 'h23' as const,
          timeZoneName: 'shortOffset' as const,
        }
      : {}),
    timeZone: granularity === 'hour' ? timeZone : 'UTC',
  }).format(new Date(date));
}

function hourStarts(date: string, timeZone: string, now: number) {
  const format = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const midnight = Date.parse(`${date}T00:00:00Z`);
  const starts: string[] = [];
  // UTC iteration preserves repeated/skipped DST hours. Quarter-hour stepping
  // also finds local xx:00 in half-hour and quarter-hour offset timezones.
  for (
    let instant = midnight - 26 * 3600_000;
    instant <= Math.min(midnight + 50 * 3600_000, now);
    instant += 15 * 60_000
  ) {
    const parts = Object.fromEntries(
      format.formatToParts(instant).map(({ type, value }) => [type, value]),
    );
    if (`${parts.year}-${parts.month}-${parts.day}` === date && parts.minute === '00')
      starts.push(new Date(instant).toISOString());
  }
  return starts;
}

export function bucketTrend<T extends { date: string }>(
  days: readonly T[],
  metrics: readonly (keyof T & string)[],
  timeZone: string,
  now = Date.now(),
): { granularity: TrendGranularity; points: T[] } {
  const granularity = pickGranularity(days.length);
  if (granularity === 'day')
    return { granularity, points: days.map((day) => ({ ...day })) };
  if (granularity === 'hour')
    return {
      granularity,
      points: days.flatMap((day) => {
        const starts = hourStarts(day.date, timeZone, now);
        const weights = starts.map((_, index) => 1 + (index % 6));
        const totalWeight = weights.reduce((sum, value) => sum + value, 0);
        let cumulative = 0;
        return starts.map((date, index) => {
          const previous = cumulative;
          cumulative += weights[index];
          return {
            ...day,
            date,
            ...Object.fromEntries(
              metrics.map((key) => {
                const total = Number(day[key]);
                return [
                  key,
                  Math.floor((total * cumulative) / totalWeight) -
                    Math.floor((total * previous) / totalWeight),
                ];
              }),
            ),
          };
        });
      }),
    };
  const weeks = new Map<string, T>();
  for (const day of days) {
    const start = new Date(`${day.date}T00:00:00Z`);
    start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
    const week = start.toISOString().slice(0, 10);
    const bucket = weeks.get(week);
    if (!bucket) weeks.set(week, { ...day });
    else
      Object.assign(
        bucket,
        Object.fromEntries(
          metrics.map((key) => [key, Number(bucket[key]) + Number(day[key])]),
        ),
      );
  }
  return { granularity, points: [...weeks.values()] };
}
